import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { campaignAPI, Campaign } from '../services/api';
import Icon from '../components/Icon';
import StatusBadge from '../components/ui/StatusBadge';
import ChannelBubble from '../components/ui/ChannelBubble';
import Empty from '../components/ui/Empty';
import Pagination from '../components/Pagination';
import { channelOf, CHANNEL_META } from '../utils/channel';
import { fmt, pct, fmtDate } from '../utils/format';
import { useT } from '../i18n/LanguageContext';

const ITEMS_PER_PAGE = 10;

type Filter = 'all' | 'draft' | 'scheduled' | 'sending' | 'completed';

const CampaignList: React.FC = () => {
  const t = useT();
  const FILTERS: { key: Filter; label: string }[] = [
    { key: 'all', label: t('Sve', 'All') },
    { key: 'draft', label: t('Nacrti', 'Drafts') },
    { key: 'scheduled', label: t('Zakazane', 'Scheduled') },
    { key: 'sending', label: t('U toku', 'Sending') },
    { key: 'completed', label: t('Završene', 'Completed') },
  ];
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showArchived, setShowArchived] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [menu, setMenu] = useState<number | null>(null);
  const navigate = useNavigate();
  const menuRef = useRef<HTMLTableSectionElement>(null);

  useEffect(() => {
    loadCampaigns();
    // Auto-refresh every 3s so "sending" campaigns update live.
    const interval = setInterval(loadCampaigns, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  useEffect(() => {
    const onDoc = () => setMenu(null);
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  const loadCampaigns = async () => {
    try {
      const response = await campaignAPI.getAll(showArchived);
      if (response.data.success) setCampaigns(response.data.data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('Da li ste sigurni da želite da TRAJNO obrišete ovu kampanju? Ova akcija se ne može poništiti.', 'Are you sure you want to PERMANENTLY delete this campaign? This action cannot be undone.'))) {
      try {
        await campaignAPI.delete(id);
        loadCampaigns();
      } catch (err: any) {
        alert(t('Greška prilikom brisanja: ', 'Error while deleting: ') + err.message);
      }
    }
  };

  const handleClone = async (id: number) => {
    try {
      const res = await campaignAPI.clone(id);
      if (res.data.success) {
        const c = res.data.data.campaign;
        const isViber = c.channelId === 3 || !!c.viberRichContent;
        navigate(isViber ? `/campaigns/viber/${c.id}/edit` : `/campaigns/${c.id}/edit`);
      }
    } catch (err: any) {
      alert(t('Greška prilikom kloniranja: ', 'Error while cloning: ') + (err.response?.data?.error || err.message));
    }
  };

  const handleArchive = async (id: number, isArchived: boolean) => {
    const action = isArchived ? t('vratiti iz arhive', 'unarchive') : t('arhivirati', 'archive');
    if (window.confirm(t(`Da li ste sigurni da želite da ${action} ovu kampanju?`, `Are you sure you want to ${action} this campaign?`))) {
      try {
        await campaignAPI.archive(id);
        loadCampaigns();
      } catch (err: any) {
        alert(t('Greška: ', 'Error: ') + err.message);
      }
    }
  };

  const editRoute = (c: Campaign) => {
    const isViber = c.channelId === 3 || !!c.viberRichContent;
    return isViber ? `/campaigns/viber/${c.id}/edit` : `/campaigns/${c.id}/edit`;
  };

  const counts = useMemo(() => {
    const by = (s: Filter) => (s === 'all' ? campaigns.length : campaigns.filter((c) => c.status === s).length);
    return Object.fromEntries(FILTERS.map((f) => [f.key, by(f.key)])) as Record<Filter, number>;
  }, [campaigns]);

  const filtered = useMemo(
    () =>
      campaigns.filter(
        (c) =>
          (filter === 'all' || c.status === filter) &&
          c.name.toLowerCase().includes(query.toLowerCase())
      ),
    [campaigns, filter, query]
  );

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const current = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const subtitleOf = (c: Campaign) => c.subject || c.textContent || CHANNEL_META[channelOf(c)].label;

  return (
    <div className="page page-wide">
      <div className="page-head">
        <div>
          <div className="page-title">{t('Kampanje', 'Campaigns')}</div>
          <div className="page-sub">{t('Sve tvoje Email, SMS i Viber kampanje na jednom mestu.', 'All your Email, SMS and Viber campaigns in one place.')}</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/campaigns/new')}>
          <Icon name="plus" size={16} /> {t('Nova kampanja', 'New campaign')}
        </button>
      </div>

      <div className="flex items-center justify-between wrap gap-16" style={{ marginBottom: 18 }}>
        <div className="seg">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={filter === f.key ? 'active' : ''}
              onClick={() => {
                setFilter(f.key);
                setCurrentPage(1);
              }}
            >
              {f.label} <span className="dim tnum" style={{ marginLeft: 4 }}>{counts[f.key]}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-16 wrap">
          <label className="flex items-center gap-8" style={{ cursor: 'pointer', userSelect: 'none', fontSize: 13.5, color: 'var(--text-2)' }}>
            <input
              type="checkbox"
              className="row-check"
              checked={showArchived}
              onChange={(e) => {
                setShowArchived(e.target.checked);
                setCurrentPage(1);
              }}
            />
            {t('Prikaži arhivirane', 'Show archived')}
          </label>
          <div className="search">
            <Icon name="search" size={16} />
            <input value={query} onChange={(e) => { setQuery(e.target.value); setCurrentPage(1); }} placeholder={t('Pretraži kampanje', 'Search campaigns')} />
          </div>
        </div>
      </div>

      {error && (
        <div className="card card-pad" style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)', marginBottom: 16 }}>
          <Icon name="alert" size={16} /> {error}
        </div>
      )}

      <div className="card">
        {loading ? (
          <div className="card-pad muted">{t('Učitavanje…', 'Loading…')}</div>
        ) : filtered.length === 0 ? (
          <Empty
            icon="megaphone"
            title={t('Nema kampanja', 'No campaigns')}
            sub={t('Kreiraj prvu kampanju da biste počeli.', 'Create your first campaign to get started.')}
            action={
              <button className="btn btn-primary" onClick={() => navigate('/campaigns/new')}>
                <Icon name="plus" size={16} /> {t('Nova kampanja', 'New campaign')}
              </button>
            }
          />
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ paddingLeft: 22 }}>{t('Kampanja', 'Campaign')}</th>
                  <th>{t('Kanal', 'Channel')}</th>
                  <th>{t('Status', 'Status')}</th>
                  <th>{t('Primaoci', 'Recipients')}</th>
                  <th style={{ width: 200 }}>{t('Isporučeno', 'Delivered')}</th>
                  <th>{t('Kreirano', 'Created')}</th>
                  <th />
                </tr>
              </thead>
              <tbody ref={menuRef}>
                {current.map((c) => {
                  const ch = channelOf(c);
                  const delivered = c.sentCount || 0;
                  return (
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/campaigns/${c.id}`)}>
                      <td style={{ paddingLeft: 22 }}>
                        <div className="flex items-center gap-12">
                          <ChannelBubble channel={ch} size="sm" />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                              {c.name}
                              {c.archived && <span className="chip" style={{ height: 22 }}>{t('Arhivirano', 'Archived')}</span>}
                            </div>
                            <div className="dim" style={{ fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>
                              {subtitleOf(c)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`ch-tag ${ch}`}>
                          <Icon name={CHANNEL_META[ch].icon} size={14} /> {CHANNEL_META[ch].label}
                        </span>
                      </td>
                      <td><StatusBadge status={c.status} /></td>
                      <td className="tnum" style={{ fontWeight: 600 }}>{c.totalRecipients ? fmt(c.totalRecipients) : '—'}</td>
                      <td>
                        {c.totalRecipients > 0 ? (
                          <div style={{ minWidth: 160 }}>
                            <div className="flex items-center justify-between" style={{ fontSize: 12, marginBottom: 5 }}>
                              <span className="tnum muted">{fmt(delivered)} {t('isp.', 'dlv.')}</span>
                              <span className="tnum" style={{ fontWeight: 700, color: 'var(--brand-600)' }}>
                                {pct(delivered, c.totalRecipients)}%
                              </span>
                            </div>
                            <div className="progress thin"><span style={{ width: pct(delivered + (c.failedCount || 0), c.totalRecipients) + '%' }} /></div>
                          </div>
                        ) : (
                          <span className="dim">—</span>
                        )}
                      </td>
                      <td className="muted" style={{ fontSize: 13 }}>{fmtDate(c.createdAt)}</td>
                      <td style={{ position: 'relative', textAlign: 'right', paddingRight: 18 }} onClick={(e) => e.stopPropagation()}>
                        <button className="icon-btn" aria-label={t('Akcije', 'Actions')} onClick={(e) => { e.stopPropagation(); setMenu(menu === c.id ? null : c.id); }}>
                          <Icon name="more" size={18} />
                        </button>
                        {menu === c.id && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            style={{ position: 'absolute', right: 18, top: 44, zIndex: 20, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', boxShadow: 'var(--sh-lg)', padding: 6, width: 184, textAlign: 'left' }}
                          >
                            <MenuItem icon="eye" label={t('Pregled', 'View')} onClick={() => { setMenu(null); navigate(`/campaigns/${c.id}`); }} />
                            {c.status === 'draft' && (
                              <MenuItem icon="edit" label={t('Izmeni', 'Edit')} onClick={() => { setMenu(null); navigate(editRoute(c)); }} />
                            )}
                            <MenuItem icon="copy" label={t('Dupliraj', 'Duplicate')} onClick={() => { setMenu(null); handleClone(c.id); }} />
                            {c.status !== 'sending' && (
                              <MenuItem icon="inbox" label={c.archived ? t('Vrati iz arhive', 'Unarchive') : t('Arhiviraj', 'Archive')} onClick={() => { setMenu(null); handleArchive(c.id, c.archived); }} />
                            )}
                            {c.status !== 'sending' && (
                              <MenuItem icon="trash" label={t('Obriši', 'Delete')} danger onClick={() => { setMenu(null); handleDelete(c.id); }} />
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div style={{ padding: '14px 18px' }}>
                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const MenuItem: React.FC<{ icon: any; label: string; onClick: () => void; danger?: boolean }> = ({ icon, label, onClick, danger }) => (
  <button
    className="lrow"
    style={{ width: '100%', padding: '9px 11px', border: 'none', background: 'transparent', fontWeight: 600, fontSize: 13.5, color: danger ? 'var(--danger)' : 'var(--text)', gap: 10, cursor: 'pointer' }}
    onClick={onClick}
  >
    <Icon name={icon} size={16} /> {label}
  </button>
);

export default CampaignList;
